// Photo-opportunity scoring engine for Juneau-2026.
// Spec §7.2 calls for 0-10 condition scoring per activity per time window.
//
// Inputs: an activity (from activities.json), a snapshot of weather + tide
// + sun for a single moment (and a small look-ahead window). The scorer
// blends light quality, cloud-modulated quality, visibility, tide
// alignment, sea state, and a crowd factor into a single 0-10 with a
// short list of human-readable reasons.

import { altitudeQuality, isGoldenHour, sunPosition } from './sun.js';

// ---- Cloud quality ---------------------------------------------------
// "High thin clouds boost, overcast neutral for waterfalls/forest,
// dampens for landscapes/glaciers" — spec §7.2.
//
// We model cloud_cover_low + mid + high (each 0..100 from Open-Meteo).
// 'lighting_preference' on the activity steers the curve.
function cloudQuality(weather, lightingPref) {
  const low = weather?.cloud_cover_low ?? 0;
  const mid = weather?.cloud_cover_mid ?? 0;
  const high = weather?.cloud_cover_high ?? 0;
  const total = weather?.cloud_cover ?? Math.max(low, mid, high);

  const overcast = total >= 80;
  const partlyCloudy = total >= 30 && total < 80;
  const clear = total < 30;
  const highThin = high >= 25 && low < 30 && mid < 50;

  if (lightingPref === 'soft_overcast') {
    if (overcast) return 0.95; // perfect for waterfalls / forest interior
    if (partlyCloudy) return 0.75;
    return 0.55;
  }

  if (lightingPref === 'soft_overcast_or_golden') {
    if (overcast) return 0.85;
    if (highThin) return 0.95;
    if (partlyCloudy) return 0.75;
    return 0.65;
  }

  if (lightingPref === 'clear_sun') {
    if (overcast) return 0.35; // panoramas die under flat overcast
    if (partlyCloudy && highThin) return 0.85;
    if (partlyCloudy) return 0.7;
    if (clear) return 1.0;
    return 0.6;
  }

  // 'any' / undefined
  if (highThin) return 0.85;
  if (partlyCloudy) return 0.8;
  if (clear) return 0.7;
  if (overcast) return 0.55;
  return 0.6;
}

// ---- Visibility ------------------------------------------------------
// Open-Meteo `visibility` is in meters. For elevation-subject activities
// (peaks, panoramas) low cloud base height also matters — we'd be in
// the cloud. We treat <2km as ruinous, >15km as ideal.
function visibilityFactor(weather, isElevationSubject) {
  const visM = weather?.visibility ?? 20000;
  const cloudBaseM = weather?.cloud_base ?? 5000;

  let v = 1.0;
  if (visM < 2000) v *= 0.2;
  else if (visM < 5000) v *= 0.5;
  else if (visM < 10000) v *= 0.85;

  if (isElevationSubject) {
    // The subject is up high. If the cloud base is below the subject,
    // the subject is hidden. We don't know the activity's elevation
    // precisely here; use cloud_base as a proxy (low cloud_base = bad).
    if (cloudBaseM < 500) v *= 0.4;
    else if (cloudBaseM < 1000) v *= 0.7;
    else if (cloudBaseM < 2000) v *= 0.9;
  }
  return v;
}

// ---- Tide alignment --------------------------------------------------
// Tide-sensitive activities (Mendenhall lake-edge framing, Eagle Beach
// tidal flats, the Shrine causeway) score higher near low tide; whale
// boats are agnostic. We weight tide level: lower = better, capped.
function tideFactor(tideSample, tideSensitive) {
  if (!tideSensitive || !tideSample) return 1.0;
  const ft = Number(tideSample.v);
  if (Number.isNaN(ft)) return 1.0;
  // Juneau tides span roughly -5ft to +20ft. Normalise to 0..1 where 0 is
  // a +18 spring high and 1 is a -3 minus-low.
  const norm = Math.max(0, Math.min(1, (18 - ft) / 21));
  return 0.6 + norm * 0.4;
}

// ---- Sea state ------------------------------------------------------
// Whale boats and cruise activities want calm seas. We use 10m wind
// speed (mph) as a coarse proxy. <8 mph great, 8-15 OK, 15-22 chop,
// >22 painful.
function seaStateFactor(weather, isMarine) {
  if (!isMarine) return 1.0;
  const wind = weather?.wind_speed_10m ?? 8;
  const gusts = weather?.wind_gusts_10m ?? wind;
  const ref = Math.max(wind, gusts * 0.7);
  if (ref < 8) return 1.0;
  if (ref < 15) return 0.85;
  if (ref < 22) return 0.6;
  return 0.3;
}

// ---- Precipitation --------------------------------------------------
function precipFactor(weather, lightingPref) {
  const prob = weather?.precipitation_probability ?? 0;
  const amt = weather?.precipitation ?? 0;
  // Light rain is fine for waterfall/forest activities.
  const tolerant = lightingPref === 'soft_overcast' || lightingPref === 'any';
  if (amt > 0.15) return tolerant ? 0.55 : 0.25; // soaking — gear gets wet
  if (prob >= 70) return tolerant ? 0.7 : 0.5;
  if (prob >= 40) return tolerant ? 0.85 : 0.7;
  return 1.0;
}

// ---- Crowd factor (weekday vs weekend, popularity proxy) -------------
function crowdFactor(date, activity) {
  const dow = date.getDay(); // 0=Sun, 6=Sat
  const isWeekend = dow === 0 || dow === 6;
  // Cruise-port days correlate with Tuesday-Friday peaks in Juneau,
  // but we don't have the live cruise schedule yet — defer to weekend
  // signal only. Easy / iconic trails punished a bit on weekends.
  const easyIconic = activity.tags?.includes('iconic') || activity.tags?.includes('easy');
  if (isWeekend && easyIconic) return 0.85;
  return 1.0;
}

// ---- The main scoring function --------------------------------------
//
// activity:  { lighting_preference, tide_sensitive, elevation_subject,
//              tags, type, departure_lat, departure_lng, ... }
// when:      Date — the moment we're scoring
// weather:   one hour from Open-Meteo `hourly` flattened to a sample
// tideSample: a single NOAA `predictions` row { t, v }
// sunHint:   optional pre-computed { altitudeDeg } at `when`
// originLat/Lng: for sun position when activity has no lat/lng of its own
//
// Returns { score: 0..10, factors: {...}, reasons: [string] }
export function scoreActivityAt({
  activity,
  when,
  weather,
  tideSample,
  originLat,
  originLng,
  sunHint,
}) {
  const lat = activity.lat ?? activity.departure_lat ?? originLat;
  const lng = activity.lng ?? activity.departure_lng ?? originLng;

  const altitudeDeg = sunHint?.altitudeDeg ?? sunPosition(when, lat, lng).altitudeDeg;
  const altQuality = altitudeQuality(altitudeDeg);
  const cloud = cloudQuality(weather, activity.lighting_preference);
  const golden = isGoldenHour(when, lat, lng);

  const isMarine = activity.type === 'tour' && (activity.tags || []).includes('marine');
  const tide = tideFactor(tideSample, activity.tide_sensitive);
  const sea = seaStateFactor(weather, isMarine);
  const visibility = visibilityFactor(weather, activity.elevation_subject);
  const precip = precipFactor(weather, activity.lighting_preference);
  const crowd = crowdFactor(when, activity);

  // Combined light = altitude × cloud, with a golden-hour kicker
  let light = altQuality * cloud;
  if (golden) light = Math.min(1, light * 1.15);

  // Final 0..1 → ×10
  const raw = light * tide * sea * visibility * precip * crowd;
  const score = Math.round(raw * 100) / 10;

  const reasons = [];
  if (altitudeDeg <= 0) reasons.push('Sun below horizon');
  else if (golden === 'morning_golden') reasons.push('Morning golden-hour window');
  else if (golden === 'evening_golden') reasons.push('Evening golden-hour window');
  else if (altitudeDeg < 6) reasons.push('Soft low-angle light (twilight)');
  else if (altitudeDeg > 60) reasons.push('Sun overhead — harsh, light dampened');

  if (visibility < 0.6) reasons.push('Low visibility — subject hidden in cloud');
  if (precip < 0.5) reasons.push('Heavy rain expected');
  else if (precip < 0.85) reasons.push('Light rain possible');

  if (sea < 0.7) reasons.push('Choppy seas — boat day uncomfortable');

  if (activity.tide_sensitive && tide < 0.8) reasons.push('Tide high — flats / causeway covered');
  if (activity.tide_sensitive && tide >= 0.9) reasons.push('Tide low — flats / causeway exposed');

  if (cloud >= 0.9 && activity.lighting_preference === 'soft_overcast') {
    reasons.push('Even overcast — ideal for waterfalls / forest');
  } else if (cloud >= 0.8 && activity.lighting_preference === 'clear_sun') {
    reasons.push('Clear skies — panorama-friendly');
  } else if (cloud < 0.5 && activity.lighting_preference === 'clear_sun') {
    reasons.push('Cloud cover dampening panorama');
  }

  if (crowd < 1) reasons.push('Weekend crowd factor');

  return {
    score,
    factors: { light, cloud, altQuality, tide, sea, visibility, precip, crowd, altitudeDeg },
    reasons,
  };
}

// ---- Window scoring ------------------------------------------------
// Walk an hourly weather array, score each hour, and return the top N
// non-overlapping windows of `windowHours` length (peak 1h within the
// window represents the score). Past hours are filtered out — Today
// surfaces forward-looking windows only.
//
// Open-Meteo with `timezone=America/Juneau` and `forecast_days=3`
// returns hourly entries starting at the local midnight of the
// **previous** day, so without this filter the recommender happily
// suggests "yesterday at 7pm".
export function topWindowsForActivity({
  activity,
  weather,           // { time: [iso...], cloud_cover: [...], ... }
  tideSeries,        // [{ t, v }]
  originLat,
  originLng,
  topN = 3,
  windowHours = 2,
  now = Date.now(),
}) {
  if (!weather?.time?.length) return [];
  const samples = weather.time
    .map((iso, i) => {
      const when = new Date(iso);
      if (when.getTime() < now) return null;
      const w = pickHourly(weather, i);
      const tide = nearestTide(tideSeries, when);
      const r = scoreActivityAt({
        activity,
        when,
        weather: w,
        tideSample: tide,
        originLat,
        originLng,
      });
      return { when, ...r };
    })
    .filter(Boolean);

  // Slide windows of `windowHours` and pick peaks.
  const windows = [];
  for (let i = 0; i + windowHours <= samples.length; i += 1) {
    const slice = samples.slice(i, i + windowHours);
    const peak = slice.reduce((best, s) => (s.score > best.score ? s : best), slice[0]);
    windows.push({
      start: slice[0].when,
      end: slice[slice.length - 1].when,
      peak,
      avgScore: slice.reduce((sum, s) => sum + s.score, 0) / slice.length,
    });
  }
  windows.sort((a, b) => b.peak.score - a.peak.score || b.avgScore - a.avgScore);

  // De-dupe overlapping windows so we get spread across the horizon.
  const picked = [];
  for (const w of windows) {
    if (picked.some((p) => Math.abs(p.start.getTime() - w.start.getTime()) < windowHours * 3600_000)) {
      continue;
    }
    picked.push(w);
    if (picked.length >= topN) break;
  }
  return picked;
}

function pickHourly(hourly, i) {
  const out = {};
  for (const k of Object.keys(hourly)) {
    if (Array.isArray(hourly[k])) out[k] = hourly[k][i];
  }
  return out;
}

function nearestTide(series, when) {
  if (!series?.length) return null;
  const t = when.getTime();
  let best = series[0];
  let bestDiff = Math.abs(new Date(best.t).getTime() - t);
  for (const row of series) {
    const d = Math.abs(new Date(row.t).getTime() - t);
    if (d < bestDiff) {
      best = row;
      bestDiff = d;
    }
  }
  return best;
}
